import React from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import withStyles from '@material-ui/core/styles/withStyles';
import { Widget } from '@uploadcare/react-widget/dist/cjs';

import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import TextField from '@material-ui/core/TextField';
import Button from '@material-ui/core/Button';
import Typography from '@material-ui/core/Typography';
import Input from '@material-ui/core/Input';
import FormControl from '@material-ui/core/FormControl';
import FormLabel from '@material-ui/core/FormLabel';
import InputLabel from '@material-ui/core/InputLabel';
import Box from '@material-ui/core/Box';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Checkbox from '@material-ui/core/Checkbox';
import ButtonBase from '@material-ui/core/ButtonBase';
import { KeyboardDatePicker } from '@material-ui/pickers';
import FormHelperText from '@material-ui/core/FormHelperText';
import NumberFormat from 'react-number-format';
import Autocomplete from '@material-ui/lab/Autocomplete';

import {
  get, find, create, remove,
} from '../../feathersWrapper';
import { PersonContext } from '../../contexts/PersonContext';
import {
  handleAutocompleteChange,
  handleTextFieldChange,
  asyncHandleChange,
  handleKeyboardDatePickerChange,
  handleNumberFormatChange,
  handleUploaderChange,
  handleCheckboxChange,
  handleTransactionScopeChange,
} from '../../functions/InputHandlers';

import {
  testMatchingRule,
} from '../../functions/RegExpFunctions';

import {
  nameLabel,
  getAccountOptions,
  getDepositAccountOptions,
  getTransactionScopeOptions,
} from '../Autocomplete/Library';

import {
  getManualBasicJournal,
  getDebitJournalLine,
  getCreditJournalLine,
} from '../SearchSelect/TransactionTypeOptions';

import {
  setJournalScope,
  setInitialJournalScope,
} from '../../functions/JournalFunctions';

import TransactionScope from '../TransactionScope';

const styles = (theme) => ({
  hideWidget: {
    display: 'none',
  },
  aButton: {
    color: theme.palette.secondary.main,
    backgroundColor: 'transparent',
    border: 'none',
    cursor: 'pointer',
    textDecoration: 'underline',
    verticalAlign: 'baseline',
    display: 'inline',
    margin: 0,
    padding: 0,
  },
  notesButton: {
    color: theme.palette.secondary.main,
    backgroundColor: 'transparent',
    border: 'none',
    cursor: 'pointer',
    verticalAlign: 'baseline',
    display: 'inline',
    margin: 0,
    padding: 0,
  },
});

class AddRevenueDialogContent extends React.PureComponent {
  uploadcareWidget = React.createRef();

  constructor(props) {
    super(props);

    this.state = { loading: true };
  }

  componentDidMount() {
    this.setInitialState();
  }

  setInitialState = async () => {
    const { yodleeTransaction } = this.props;
    const {
      propertyId, unitId, propertyManagerId, bankAccountId, ruleId,
    } = this.props;
    const { partnerName, partnerSubscribed } = this.context;
    const initialState = {
      loading: false,
      submitting: false,
      autobookResult: false,
      rule: {
        createRule: false,
        matchDescription: false,
        matchAmount: false,
        descriptionContains: '',
        autobookMatchingTransactions: true,
        applyToAllAccounts: false,
      },
      displayNotesSection: false,
      error: null,
    };

    initialState.revenueAccountOptions = await getAccountOptions(this, { $or: [{ type: 'Revenue' }, { type2: 'Deferred Revenue' }] });
    const journal = getManualBasicJournal();
    journal.type = 'Revenue';
    journal.journalScopeSelect = { name: 'Property/Unit', id: 'Property/Unit' };

    if (yodleeTransaction) {
      journal.debitLines[0].accountIdSelect = yodleeTransaction.account;
      journal.debitLines[0].yodleeTransactionId = yodleeTransaction.id;
      journal.description = yodleeTransaction.description;
      journal.date = yodleeTransaction.date;
      journal.amount = yodleeTransaction.amount;
    }

    if (journal.amount && journal.amount < 5) {
      [journal.creditLines[0].accountIdSelect] = initialState.revenueAccountOptions.filter((account) => account.default === 'interestRevenue');
      [journal.journalScopeSelect] = getTransactionScopeOptions(this).filter((option) => option.id === 'Portfolio');
    } else if (journal.amount && journal.amount > 300) {
      [journal.creditLines[0].accountIdSelect] = initialState.revenueAccountOptions.filter((account) => account.default === 'rentRevenue');
    } else if (!journal.amount) {
      [journal.creditLines[0].accountIdSelect] = initialState.revenueAccountOptions.filter((account) => account.default === 'rentRevenue');
    }

    if (partnerName && (partnerSubscribed || partnerName === 'Innago') && journal.amount >= 50) {
      initialState.warnPartnerPayment = true;
      initialState.confirmUnique = false;
    }

    if (ruleId) {
      const rule = await get(this, 'imported-transaction-rules', ruleId);
      journal.creditLines[0].accountIdSelect = rule.destinationAccount;
      journal.unitId = rule.unitId;
      journal.propertyId = rule.propertyId;
      journal.entityId = rule.entityId;
      await setInitialJournalScope(journal, this);
    } else if (unitId) {
      const unit = await get(this, 'units', unitId);
      journal.propertyIdSelect = unit.property;
      journal.unitIdSelect = unit;
    } else if (propertyId) {
      const property = await get(this, 'properties', propertyId);
      journal.propertyIdSelect = property;
    } else if (propertyManagerId) {
      const propertyManager = await get(this, 'property-managers', propertyManagerId);
      journal.debitLines[0].accountIdSelect = propertyManager.account;
    } else if (bankAccountId) {
      const account = await get(this, 'accounts', bankAccountId);
      journal.debitLines[0].accountIdSelect = account;
      if (account.property) {
        journal.propertyIdSelect = account.property;
      }
    }

    initialState.journal = journal;
    initialState.depositAccountOptions = await getDepositAccountOptions(this);
    this.setState(initialState);
  };

  closeDialog = () => {
    const { closeDialog } = this.props;
    this.setState({ loading: true });
    closeDialog();
  };

  addTransaction = async (event) => {
    event.preventDefault();
    const {
      submitting, journal, rule,
      warnPartnerPayment, confirmUnique,
    } = this.state;

    const { onAddTransaction, yodleeTransaction } = this.props;
    const { organizationId } = this.context;

    if (submitting) {
      return;
    }

    if (!journal.date) {
      this.setState({ error: { message: 'Please enter a valid date for this transaction' } });
      return;
    }

    if (rule.createRule) {
      if (!rule.matchDescription && !rule.matchAmount) {
        this.setState({
          error: {
            message: 'Please select a description or amount to create a matching rule',
          },
        });
        return;
      }
      if (rule.matchDescription) {
        if (
          !testMatchingRule(rule.descriptionContains, yodleeTransaction.description)
        ) {
          this.setState({
            error: {
              message: `The imported transaction description does not contain the matching rule text. 
              Please update the rule so it is contained within the text: ${yodleeTransaction.description}`,
            },
          });
          return;
        }
      }
    }

    this.setState({ submitting: true });

    setJournalScope(journal);

    if (warnPartnerPayment && !confirmUnique) {
      const partnerAccountsResult = await find(this, 'accounts', {
        query: {
          organizationId,
          type2: 'Partner',
        },
      });

      // find partner account transactions for the selected property and unit
      const journalLineMatchQurey = {
        organizationId,
        accountId: partnerAccountsResult.data.map((account) => account.id),
        propertyId: journal.propertyId,
        unitId: journal.unitId,
        startDate: moment(journal.date).subtract(21, 'days').format('YYYY-MM-DD'),
        endDate: moment(journal.date).add(4, 'days').format('YYYY-MM-DD'),
      };
      const journalLineMatch = await find(this, 'journal-lines', { query: journalLineMatchQurey });

      if (journalLineMatch.data.length) {
        this.setState({
          submitting: false,
          confirmUnique: true,
          partnerTransactions: journalLineMatch.data,
        });
        return;
      }
    }

    journal.organizationId = organizationId;
    journal.debitLines[0].debit = journal.amount;
    journal.debitLines[0].accountId = journal.debitLines[0].accountIdSelect.id;
    journal.creditLines[0].credit = journal.amount;
    journal.creditLines[0].accountId = journal.creditLines[0].accountIdSelect.id;
    journal.type = 'Revenue';
    journal.journalLines = journal.debitLines.concat(journal.creditLines);
    if (journal.amount < 0) {
      // book as a refund
      journal.type = 'Refund Issued';
      const newJournalLines = [];
      journal.journalLines.forEach((line) => {
        if (line.type === 'credit') {
          const newLine = getDebitJournalLine();
          newLine.debit = -line.credit;
          newLine.accountId = line.accountId;
          newJournalLines.push(newLine);
        } else {
          const newLine = getCreditJournalLine();
          newLine.credit = -line.debit;
          newLine.accountId = line.accountId;
          newJournalLines.push(newLine);
        }
      });
      journal.journalLines = newJournalLines;
    }

    // errors will be displayed within the dialog rather than throwing to the error boundary
    create(this, 'journals', journal, true)
      .then(async (journalResult) => {
        if (rule.createRule) {
          const ruleSubmit = {
            type: 'Revenue',
            organizationId,
            destinationAccountId: journal.creditLines[0].accountId,
            entityId: journal.entityId,
            propertyId: journal.propertyId,
            unitId: journal.unitId,
            transactionAmount: rule.matchAmount ? journal.amount : null,
            descriptionContains: rule.matchDescription ? rule.descriptionContains : '',
            autobookMatchingTransactions: rule.autobookMatchingTransactions,
            analysisAccountId: rule.applyToAllAccounts ? null : yodleeTransaction.account.id,
          };

          await create(this, 'imported-transaction-rules', ruleSubmit, true)
            .then(async (ruleResult) => {
              if (ruleSubmit.autobookMatchingTransactions) {
                const automationResult = await create(this, 'automations', {
                  organizationId: ruleSubmit.organizationId,
                  automationName: 'bookRuleMatching',
                  ruleId: ruleResult.id,
                });
                onAddTransaction(journalResult);
                if (automationResult.newTransactions) {
                  this.setState({
                    autobookResult: true,
                    autobookedTransactionCount: automationResult.newTransactions,
                  });
                } else {
                  this.closeDialog();
                }
              } else {
                onAddTransaction(journalResult);
                this.closeDialog();
              }
            })
            .catch((error) => {
              remove(this, 'journals', journalResult.id, true);
              this.setState({ error });
              this.setState({ submitting: false });
            });
        } else {
          onAddTransaction(journalResult);
          this.closeDialog();
        }
      })
      .catch((error) => {
        this.setState({ error });
        this.setState({ submitting: false });
      });
  };

  render() {
    const {
      classes, ruleId, yodleeTransaction, resetTransactionType,
    } = this.props;
    const { partnerName } = this.context;
    const {
      loading,
      error,
      journal,
      rule,
      revenueAccountOptions,
      depositAccountOptions,
      warnPartnerPayment,
      autobookResult,
      autobookedTransactionCount,
      confirmUnique,
      partnerTransactions,
      displayNotesSection,
    } = this.state;

    if (loading) {
      return null;
    }

    if (autobookResult) {
      return (
        <>
          <DialogTitle id="alert-dialog-title">
            Rule Created
          </DialogTitle>
          <DialogContent>
            <Typography variant="body1">
              {`Your rule was created. ${autobookedTransactionCount} additional transactions matched the template and were booked automatically.`}
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={this.closeDialog} color="primary" variant="contained" disableElevation>
              Close
            </Button>
          </DialogActions>
        </>
      );
    }

    if (confirmUnique) {
      return (
        <>
          <DialogTitle id="alert-dialog-title">
            Warning: Possible Duplicate
          </DialogTitle>
          <DialogContent>
            <Typography variant="body1">
              {`The transactions listed below were already imported into REI Hub for this ${journal.unitId ? 'unit' : 'property'} from our partner ${partnerName}. 
              Paid invoices (both electronic and manual) from ${partnerName} automatically sync into REI Hub. 
              If you marked these funds as paid in ${partnerName}, use the '${partnerName} Deposit Template' instead to avoid double booking revenue.`}
            </Typography>
            <ul>
              {partnerTransactions.map((match) => (
                <li
                  key={match.id}
                >
                  <>
                    {match.journal.description}
                    <Box color="text.secondary">
                      {moment(match.journal.date).format('M/D/YYYY')}
                      <Box px={2} component="span">|</Box>
                      <NumberFormat
                        displayType="text"
                        value={match.credit ? match.credit : match.debit}
                        thousandSeparator
                        prefix="$"
                        decimalScale={2}
                        fixedDecimalScale
                      />
                    </Box>
                  </>
                </li>
              ))}
            </ul>
            <Box
              mt={3}
              marginBottom={2}
              marginX="auto"
              textAlign="center"
            >
              <Box marginBottom={1}>
                <Button
                  color="secondary"
                  variant="outlined"
                  size="large"
                  fullWidth
                  onClick={() => {
                    resetTransactionType('Partner Deposit');
                  }}
                >
                  Use Deposit Template
                </Button>
              </Box>
              <Button
                color="secondary"
                variant="outlined"
                size="large"
                fullWidth
                onClick={this.addTransaction}
              >
                Save Additional Revenue
              </Button>
              <Button
                color="primary"
                onClick={this.closeDialog}
              >
                Cancel
              </Button>
            </Box>
          </DialogContent>
        </>
      );
    }

    return (
      <form onSubmit={this.addTransaction}>
        <DialogTitle id="alert-dialog-title">
          Add Revenue
        </DialogTitle>
        <DialogContent>
          {warnPartnerPayment && (
            <Box
              border={1}
              borderColor="grey.500"
              borderRadius="borderRadius"
              padding={2}
              marginY={2}
            >
              <Typography variant="body2">
                {`All paid invoices are automatically synced from ${partnerName} into REI Hub. `}
                <ButtonBase
                  component="span"
                  className={classes.aButton}
                  onClick={() => {
                    resetTransactionType('Partner Deposit');
                  }}
                >
                  Use the deposit template instead
                </ButtonBase>
                {` if these funds have been marked as received in ${partnerName} to avoid double booking revenue.`}
              </Typography>
            </Box>
          )}
          {yodleeTransaction && (
            <FormControl margin="dense" fullWidth>
              <FormLabel shrink style={{ position: 'relative' }} component={InputLabel}>
                Transaction
              </FormLabel>
              <Typography>
                {moment(yodleeTransaction.date).format('M/D/YYYY')}
                <Box px={2} component="span">|</Box>
                <NumberFormat
                  displayType="text"
                  value={yodleeTransaction.amount}
                  thousandSeparator
                  prefix="$"
                  decimalScale={2}
                  fixedDecimalScale
                />
                <Box px={2} component="span">|</Box>
                {yodleeTransaction.account.name}
              </Typography>
            </FormControl>
          )}
          <KeyboardDatePicker
            label="Date"
            format="MM/DD/YYYY"
            placeholder="MM/DD/YYYY"
            value={journal.date}
            onChange={handleKeyboardDatePickerChange('nested_journal_date', this)}
            margin="dense"
            fullWidth
            clearable
            required
          />
          <FormControl margin="dense" fullWidth>
            <InputLabel required>
              Amount
            </InputLabel>
            <NumberFormat
              value={journal.amount}
              required
              thousandSeparator
              prefix="$"
              decimalScale={2}
              fixedDecimalScale
              onValueChange={handleNumberFormatChange('nested_journal_amount', this)}
              customInput={Input}
            />
            {journal.amount < 0 && (
              <FormHelperText>
                <Typography color="error" variant="inherit">
                  Entering a negative number will create this transaction as a refund issued.
                </Typography>
              </FormHelperText>
            )}
          </FormControl>
          <TransactionScope
            journal={journal}
            transactionScopeChange={(newScopeValues) => {
              handleTransactionScopeChange(journal, newScopeValues, this);
            }}
          />
          <Autocomplete
            options={revenueAccountOptions}
            getOptionLabel={nameLabel}
            value={journal.creditLines[0].accountIdSelect}
            onChange={handleAutocompleteChange(
              'nested_journal_creditLines_0_accountIdSelect',
              this,
            )}
            getOptionSelected={(option, value) => option.id === value.id}
            renderInput={(params) => (
              <TextField
                {...params /* eslint-disable-line react/jsx-props-no-spreading */}
                margin="dense"
                label="Revenue Account"
                placeholder="Type to Search"
                fullWidth
                required
              />
            )}
          />
          {!yodleeTransaction && (
            <Autocomplete
              options={depositAccountOptions}
              getOptionLabel={nameLabel}
              value={journal.debitLines[0].accountIdSelect}
              onChange={handleAutocompleteChange(
                'nested_journal_debitLines_0_accountIdSelect',
                this,
              )}
              getOptionSelected={(option, value) => option.id === value.id}
              renderInput={(params) => (
                <TextField
                  {...params /* eslint-disable-line react/jsx-props-no-spreading */}
                  margin="dense"
                  label="Deposit Account"
                  placeholder="Type to Search"
                  fullWidth
                  required
                />
              )}
            />
          )}
          <TextField
            label="Description (optional)"
            fullWidth
            margin="dense"
            InputProps={{
              value: journal.description,
              name: 'nested_journal_description',
              onChange: handleTextFieldChange(this),
            }}
          />
          {displayNotesSection && (
            <TextField
              label="Additional Notes (optional)"
              fullWidth
              multiline
              minRows="3"
              maxRows="8"
              variant="filled"
              color="secondary"
              margin="dense"
              InputProps={{
                value: journal.notes,
                name: 'nested_journal_notes',
                onChange: handleTextFieldChange(this),
              }}
            />
          )}
          {!displayNotesSection && (
            <Box pb={2} pt={2}>
              <ButtonBase
                component="div"
                className={classes.notesButton}
                onClick={() => {
                  this.setState({ displayNotesSection: true });
                }}
              >
                Add Additional Notes
              </ButtonBase>
            </Box>
          )}
          {yodleeTransaction && !ruleId && (
            <FormControl margin="dense">
              <FormControlLabel
                control={<Checkbox checked={rule.createRule} onChange={handleCheckboxChange(this)} name="nested_rule_createRule" />}
                label="Create a new transaction matching rule"
              />
            </FormControl>
          )}
          {rule.createRule && (
            <Box
              border={1}
              borderColor="grey.500"
              borderRadius="borderRadius"
              padding={2}
              marginY={2}
            >
              <FormControl margin="none" fullWidth>
                <FormControlLabel
                  control={<Checkbox checked={rule.matchDescription} onChange={handleCheckboxChange(this)} name="nested_rule_matchDescription" />}
                  label="Match by description"
                />
              </FormControl>
              {rule.matchDescription && (
                <>
                  <Typography variant="body2">
                    {`Enter a matching phrase contained in the imported description. 
                      The imported description was: ${yodleeTransaction.description}`}
                  </Typography>
                  <TextField
                    label="Description Contains"
                    fullWidth
                    required
                    margin="dense"
                    InputProps={{
                      value: rule.descriptionContains,
                      name: 'nested_rule_descriptionContains',
                      onChange: handleTextFieldChange(this),
                    }}
                  />
                </>
              )}
              <FormControl margin="none" fullWidth>
                <FormControlLabel
                  control={<Checkbox checked={rule.matchAmount} onChange={handleCheckboxChange(this)} name="nested_rule_matchAmount" />}
                  label="Match by amount"
                />
              </FormControl>
              <FormControl margin="none" fullWidth>
                <FormControlLabel
                  control={<Checkbox checked={rule.autobookMatchingTransactions} onChange={handleCheckboxChange(this)} name="nested_rule_autobookMatchingTransactions" />}
                  label="Automatically book matching transactions"
                />
              </FormControl>
              <FormControl margin="none" fullWidth>
                <FormControlLabel
                  control={<Checkbox checked={rule.applyToAllAccounts} onChange={handleCheckboxChange(this)} name="nested_rule_applyToAllAccounts" />}
                  label="Apply this rule to all accounts"
                />
              </FormControl>
              {!rule.applyToAllAccounts && (
                <Typography variant="body2">
                  {`This rule will be applied only to transactions in ${yodleeTransaction.account.name}`}
                </Typography>
              )}
            </Box>
          )}
          <FormControl margin="dense">
            <FormLabel shrink style={{ position: 'relative' }} component={InputLabel}>
              Attachments
            </FormLabel>
            <div className={journal.attachmentURL ? null : classes.hideWidget}>
              <Widget
                ref={this.uploadcareWidget}
                multiple
                tabs="file url camera"
                value={journal.attachmentURL}
                onChange={handleUploaderChange('nested_journal_attachmentURL', this)}
              />
            </div>
            {!journal.attachmentURL && (
              <Button
                color="primary"
                variant="outlined"
                className={classes.editImageButton}
                onClick={() => {
                  this.uploadcareWidget.current.openDialog();
                }}
              >
                Click to Add Pictures or Files
              </Button>
            )}
            {journal.attachmentURL && (
              <Button
                color="primary"
                className={classes.editImageButton}
                onClick={async () => {
                  await asyncHandleChange('nested_journal_attachmentURL', '', this);
                  this.uploadcareWidget.current.reloadInfo();
                }}
              >
                Remove All Attachments
              </Button>
            )}
          </FormControl>
          <Typography color="error">{error && error.message}</Typography>
        </DialogContent>
        <DialogActions>
          <Button type="submit" variant="contained" color="primary" disableElevation>
            Save Revenue
          </Button>
          <Button onClick={this.closeDialog} color="primary">
            Cancel
          </Button>
        </DialogActions>
      </form>
    );
  }
}

AddRevenueDialogContent.contextType = PersonContext;

AddRevenueDialogContent.defaultProps = {
  yodleeTransaction: null,
  unitId: null,
  propertyId: null,
  propertyManagerId: null,
  bankAccountId: null,
  ruleId: null,
  resetTransactionType: () => {},
};

AddRevenueDialogContent.propTypes = {
  classes: PropTypes.objectOf(PropTypes.string).isRequired,
  closeDialog: PropTypes.func.isRequired,
  onAddTransaction: PropTypes.func.isRequired,
  yodleeTransaction: PropTypes.objectOf(PropTypes.any),
  unitId: PropTypes.node,
  propertyId: PropTypes.node,
  propertyManagerId: PropTypes.node,
  bankAccountId: PropTypes.node,
  ruleId: PropTypes.node,
  resetTransactionType: PropTypes.func,
};

export default withStyles(styles)(AddRevenueDialogContent);
